import { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AccessDenied from "@/components/common/AccessDenied";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PBSPeoplePicker } from "@/components/helper/PBSPeoplePicker";
import MyProviderData from "@/components/widgets/MyProviderData/MyProviderData";
import { useApiClient } from "@/context/ApiContext";
import { CurrentUserContext } from "@/context/CurrentUserContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { getByLoginName } from "@/services/user/User";
import { User } from "@/types/common/user";

const ProviderDataPage = () => {
    useDocumentTitle('Provider Data');
    const { currentUser, accessControl } = useContext(CurrentUserContext);
    const api = useApiClient();
    const canPickPersona = accessControl.canRead('mpd_impersonation');

    // The persona lives in the URL (#/provider-data?username=jdoe, a login without its domain) so
    // a link can open someone's provider data directly. It is only honored for mpd_impersonation.
    const [searchParams, setSearchParams] = useSearchParams();
    const personaLogin = canPickPersona ? searchParams.get('username')?.trim() || null : null;

    // The persona is deliberately page-local: mpd_impersonation grants a view of another
    // user's provider data only, so it must never reach the app-wide CurrentUserContext.
    const [persona, setPersona] = useState<User | null>(null);

    useEffect(() => {
        if (!personaLogin) {
            setPersona(null);
            return;
        }

        let cancelled = false;
        getByLoginName(personaLogin, api)
            .then((response) => {
                const user = response.data.value?.[0];
                if (cancelled) return;
                if (user) user.LoginName = personaLogin; // match the login name we searched for
                setPersona(user ?? null);
            })
            .catch((err) => {
                console.error("Error fetching user by login name:", err);
                if (!cancelled) setPersona(null);
            });

        // a persona picked while the previous one is still loading must not be overwritten by it
        return () => {
            cancelled = true;
        };
    }, [personaLogin, api]);

    const onPersonaChange = useCallback((loginName: string) => {
        const selected = loginName.split(',')[0]?.trim();
        setSearchParams(selected ? { username: selected } : {}, { replace: true });
    }, [setSearchParams]);

    if (!accessControl.canRead('widget_provider_data')) {
        return <AccessDenied />;
    }

    const subject = persona ?? currentUser;

    // Full impersonation carries the employee details with it; mpd_impersonation does not,
    // so those viewers get everything on the page except another user's employee block.
    const isViewingOther =
        !!persona && persona.LoginName?.toLowerCase() !== currentUser?.LoginName?.toLowerCase();
    const hideEmployeeInfo = isViewingOther && !accessControl.canRead('impersonation');

    return (
        <div className="p-4">
            {canPickPersona && (
                <Card className="mb-4">
                    <CardContent className="p-4">
                        <PBSPeoplePicker label="Select Persona" onChange={onPersonaChange} />
                        {subject?.FullName && (
                            <Label className="text-foreground">
                                <b>{subject.FullName}</b><br />{subject.Role}, {subject.Region}
                            </Label>
                        )}
                    </CardContent>
                </Card>
            )}

            <MyProviderData user={subject} hideEmployeeInfo={hideEmployeeInfo} />
        </div>
    );
};

export default ProviderDataPage;
